import {Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidatorFn} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {MatAutocomplete, MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {MatChipInputEvent} from '@angular/material/chips';
import {Observable} from 'rxjs';
import {startWith, map} from 'rxjs/operators';

@Component({
    selector: 'emm-add-component-dialog',
    templateUrl: './add-component-dialog.component.html',
    styleUrl: './add-component-dialog.component.scss',
    standalone: false
})
export class AddComponentDialogComponent implements OnInit{

    private readonly COMPONENT_CONTROL_NAME = 'newComponent';

    form: FormGroup;
    allowedComponents: any[] = this.data?.allowedComponents || [];
    selectedComponents = [];
    existingComponents: string[] = [];
    rejectedPasteTags: string[] = [];
    readonly separatorKeysCodes: number[] = [ENTER, COMMA];

    @ViewChild('componentInput') optionInput!: ElementRef<HTMLInputElement>;
    @ViewChild('auto') autocomplete: MatAutocomplete;

    filteredComponents: Observable<string[]>;

    get newComponentControl(): FormControl {
        return this.form.get(this.COMPONENT_CONTROL_NAME) as FormControl;
    }

    constructor(
        public dialogRef: MatDialogRef<AddComponentDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { existingComponents?: any[], allowedComponents?: any[] }
    ) {}

    ngOnInit() {
        // TODO: Add logic that only allows relevant components to be added.
        //  This should be based on the class of the item that opened this dialog and we may need an autocomplete
        //  rather than a text field.

        if (this.data?.existingComponents) {
            this.existingComponents = this.data.existingComponents
                .map(c => typeof c === 'string' ? c.trim() : this.extractTag(c))
                .filter(Boolean)
                .map(tag => this.normalizeTag(tag));
        }

        this.buildForm();
        this.setupFilteredComponents();
    }

    private extractTag(candidate: any): string {
        if (!candidate) {
            return '';
        }
        return candidate.itemTag || candidate.tag || '';
    }

    buildForm() {
        this.form = new FormGroup({
            [this.COMPONENT_CONTROL_NAME]: new FormControl('', [this.requiredIfNoSelected(), this.duplicateComponentValidator()])
        });
    }

    private requiredIfNoSelected(): ValidatorFn {
        return (control: AbstractControl): Record<string, any> | null => {
            const hasSelected = Array.isArray(this.selectedComponents) && this.selectedComponents.length > 0;
            const value = control.value;
            if (hasSelected) {
                // If user has already selected one or more chips, input is not required
                return null;
            }
            // Otherwise enforce required behavior
            const isEmpty = value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
            return isEmpty ? { required: true } : null;
        };
    }

    private normalizeTag(tag: string): string {
        return tag.trim().toUpperCase();
    }

    duplicateComponentValidator(): ValidatorFn {
        return (control: AbstractControl): Record<string, any> | null => {
            const inputValue = control.value?.trim();
            if (!inputValue) {
                return null;
            }

            const newTags = inputValue
                .split(',')
                .map(tag => this.normalizeTag(tag))
                .filter(Boolean);

            const selectedNormalized = this.selectedComponents.map(s => this.normalizeTag(s));

            const duplicates = newTags.filter(tag => this.existingComponents.includes(tag) || selectedNormalized.includes(tag));

            return duplicates.length > 0
                ? { duplicateComponent: { value: duplicates.join(', ') } }
                : null;
        };
    }

    close(): void {
        this.dialogRef.close();
    }

    save() {
        if (this.form.valid) {
            const result = this.selectedComponents.length ? this.selectedComponents : (this.newComponentControl.value ? this.newComponentControl.value.split(',').map(v => v.trim()).filter(Boolean) : []);
            this.dialogRef.close(result);
            // this.dialogRef.close(this.selectedComponents);
        }
    }

    add(event: MatChipInputEvent): void {
        if (this.autocomplete?.isOpen) {
            return;
        }
        const value = (event.value || '').trim();

        if (value) {
            this.addComponent(value);
        }

        if (event.input) {
            event.input.value = '';
        }
        this.clearFormControl();
    }

    onPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const text = event.clipboardData?.getData('text') ?? '';
        const tags = text.split(',').map(t => t.trim().toUpperCase()).filter(Boolean);
        const allowedTags = this.getAllowedTags();

        if (allowedTags.length > 0) {
            this.rejectedPasteTags = tags.filter(tag => !allowedTags.includes(tag));
            tags.filter(tag => allowedTags.includes(tag)).forEach(tag => this.addComponent(tag));
        } else {
            this.rejectedPasteTags = [];
            tags.forEach(tag => this.addComponent(tag));
        }

        this.newComponentControl.setValue('');
        if (this.optionInput) {
            this.optionInput.nativeElement.value = '';
        }
    }

    private getAllowedTags(): string[] {
        return this.allowedComponents
            .map(c => this.extractTag(c))
            .filter(Boolean)
            .map(tag => this.normalizeTag(tag));
    }

    remove(option: string): void {
        const index = this.selectedComponents.indexOf(option);
        if (index >= 0) {
            this.selectedComponents.splice(index, 1);
            this.form.markAsDirty();
            this.newComponentControl.updateValueAndValidity();
        }
    }

    selected(event: MatAutocompleteSelectedEvent): void {
        this.addComponent(event.option.viewValue);
        if (this.optionInput && this.optionInput.nativeElement) {
            this.optionInput.nativeElement.value = '';
        }
        this.clearFormControl();
    }

    private addComponent(value: string): void {
        if (value) {
            const normalized = this.normalizeTag(value);
            const alreadyExisting = this.existingComponents.includes(normalized) || this.selectedComponents.map(s => this.normalizeTag(s)).includes(normalized);
            if (!this.selectedComponents.includes(value) && !alreadyExisting) {
                this.selectedComponents.push(value);
                this.form.markAsDirty();
                this.newComponentControl.updateValueAndValidity();
            }
        }
    }

    private clearFormControl(): void {
        this.newComponentControl.setValue(null);
        this.newComponentControl.setErrors(null);
        this.newComponentControl.updateValueAndValidity();
        this.form.markAsDirty();
    }

    private setupFilteredComponents(): void {
        this.filteredComponents = this.newComponentControl.valueChanges.pipe(
            startWith(''),
            map(value => this._filter(value || ''))
        );
    }

    private _filter(value: string): string[] {
        const filterValue = (value || '').toUpperCase().trim();
        const selectedNorm = this.selectedComponents.map(s => this.normalizeTag(s));

        return this.allowedComponents
            .map(c => this.extractTag(c))
             .filter(Boolean)
             .filter(c => {
                 const tag = this.normalizeTag(c);
                 const already = this.existingComponents.includes(tag) || selectedNorm.includes(tag);
                 if (filterValue === '') {
                     return !already;
                 }
                 return !already && tag.includes(filterValue);
             });
    }
}
